import { CheckIcon, ChevronDownIcon, SearchIcon, XIcon } from 'lucide-react'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { type ComponentProps, type ReactNode, useId, useState } from 'react'

import { type DocumentDirection, useDocumentDirection } from '../../hooks/use-document-direction'
import { fieldTriggerClass } from '../../lib/field-trigger'
import { nextEnabledIndex, rankOptions } from '../../lib/option-match'
import { cn } from '../../lib/utils'
import { Input } from './input'

export interface ComboboxOption {
  value: string
  label: string
  displayLabel?: string
  /** A muted second line under the label, e.g. a code. */
  description?: string
  /** Searched, never shown: a code that finds a row its label does not spell out. */
  keywords?: string[]
  /** Indents the row in the list by `depth` rem; the trigger stays flat. */
  depth?: number
  /** Listed but not selectable, e.g. a group. Arrow keys skip it. */
  disabled?: boolean
  title?: string
  'aria-label'?: string
}

export interface ComboboxProps {
  id?: string
  value: string
  onValueChange: (value: string) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  clearable?: boolean
  clearLabel?: string
  /** Fired on every keystroke so a caller can filter server-side (Task 8). */
  onSearchChange?: (query: string) => void
  /**
   * `false` shows every option as given: the caller already filtered them
   * server-side, possibly on fields the label does not show.
   */
  filter?: boolean
  /** Rendered under the option list, e.g. a "load more" control. */
  footer?: ReactNode
  className?: string
  /** The field cannot be opened, e.g. until another field it depends on is chosen. */
  disabled?: boolean
  dir?: DocumentDirection
  onCloseAutoFocus?: ComponentProps<typeof PopoverPrimitive.Content>['onCloseAutoFocus']
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
  'data-testid'?: string
}

/** One row of the list: 36px, `--accent` under the pointer or focus. */
const optionClass = cn(
  'flex min-h-9 w-full cursor-default items-center gap-2 rounded-sm px-2.5 py-2 text-start text-body outline-hidden select-none',
  'hover:bg-accent focus-visible:bg-accent',
)

/** The chosen row: grey ink plus a tick, kept under the pointer. */
const selectedOptionClass =
  'bg-brand-subtle text-brand-subtle-foreground hover:bg-brand-subtle focus-visible:bg-brand-subtle'

/** A listed row that cannot be picked: muted ink, no fill even under the pointer. */
const disabledOptionClass =
  'cursor-not-allowed text-muted-foreground hover:bg-transparent focus-visible:bg-transparent'

function Combobox({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  clearable = false,
  clearLabel,
  onSearchChange,
  filter = true,
  footer,
  className,
  dir: providedDir,
  onCloseAutoFocus,
  ...props
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(-1)
  const listId = useId()
  const selected = options.find((option) => option.value === value)
  // Portalled content lands on document.body, outside any wrapper in the tree —
  // it must carry the direction itself, including after a locale switch.
  const documentDirection = useDocumentDirection()
  const dir = providedDir ?? documentDirection

  const filtered = filter ? rankOptions(options, query) : options

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setQuery('')
          setActiveIndex(-1)
        }
      }}
    >
      <div className={cn('relative inline-flex w-64 items-center', className)}>
        <PopoverPrimitive.Trigger asChild>
          {/* A native button, not the `Button` primitive. This control picks a
              value into a form, so it wears the field skin the `Select` and the
              `Input` beside it wear. See `lib/field-trigger.ts`. */}
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={open ? listId : undefined}
            data-placeholder={selected ? undefined : ''}
            className={fieldTriggerClass(clearable && selected && 'pe-3')}
            {...props}
          >
            {/* A long choice is cut to fit; the full text shows on hover. */}
            <span
              className={cn('min-w-0 truncate text-start', clearable && selected && 'pe-5')}
              title={selected ? (selected.displayLabel ?? selected.label) : undefined}
            >
              {selected ? (selected.displayLabel ?? selected.label) : placeholder}
            </span>
            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverPrimitive.Trigger>
        {clearable && selected ? (
          <button
            type="button"
            aria-label={clearLabel ?? 'Clear selection'}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onValueChange('')
            }}
            className={cn(
              'absolute end-8 top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded-sm',
              'text-muted-foreground transition-ui hover:bg-accent hover:text-foreground',
              'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
            )}
          >
            <XIcon className="size-3.5" />
          </button>
        ) : null}
      </div>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          dir={dir}
          align="start"
          sideOffset={4}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={(event) => {
            event.preventDefault()
            setActiveIndex(-1)
            setOpen(false)
          }}
          data-slot="combobox-content"
          className={cn(
            'z-50 w-[max(16rem,var(--radix-popover-trigger-width))] max-w-[calc(100vw-2rem)] overflow-hidden p-0',
            // The popover elevation (DESIGN.md §4).
            'rounded-md border border-border bg-popover text-popover-foreground shadow-popover',
            'duration-[var(--motion-base)] data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        >
          <div className="border-b border-border p-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                role="combobox"
                aria-label={searchPlaceholder}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-expanded={open}
                aria-activedescendant={
                  activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined
                }
                value={query}
                onChange={(event) => {
                  const next = event.target.value
                  setQuery(next)
                  setActiveIndex(-1)
                  onSearchChange?.(next)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    if (filtered.length === 0) return
                    event.preventDefault()
                    const direction = event.key === 'ArrowDown' ? 1 : -1
                    setActiveIndex((current) => nextEnabledIndex(filtered, current, direction))
                  } else if (event.key === 'Enter') {
                    const active = filtered[activeIndex]
                    if (!active || active.disabled) return
                    event.preventDefault()
                    onValueChange(active.value)
                    setOpen(false)
                  }
                }}
                placeholder={searchPlaceholder}
                className="ps-8"
              />
            </div>
          </div>
          <div
            id={listId}
            role="listbox"
            aria-label={props['aria-label']}
            className="max-h-60 overflow-y-auto p-1"
          >
            {clearable && selected && (
              <div role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    onValueChange('')
                    setOpen(false)
                  }}
                  className={cn(optionClass, 'text-muted-foreground hover:text-foreground')}
                >
                  <XIcon className="size-4 shrink-0" />
                  <span>{clearLabel ?? placeholder ?? 'Clear selection'}</span>
                </button>
              </div>
            )}
            {filtered.length === 0 && (
              <div role="presentation" className="px-2.5 py-2 text-body text-muted-foreground">
                {emptyLabel}
              </div>
            )}
            {filtered.map((option, index) => (
              <div key={option.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  id={`${listId}-option-${index}`}
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled || undefined}
                  aria-label={option['aria-label']}
                  title={option.title}
                  onClick={() => {
                    if (option.disabled) return
                    onValueChange(option.value)
                    setOpen(false)
                  }}
                  className={cn(
                    optionClass,
                    'justify-between data-[active=true]:bg-accent',
                    option.value === value && selectedOptionClass,
                    option.disabled && disabledOptionClass,
                  )}
                  data-active={activeIndex === index || undefined}
                >
                  {/* The indent is a margin on the content, not padding on the row, so
                      depth 0 keeps the row's own inset. */}
                  <span
                    data-slot="combobox-option-content"
                    className="flex min-w-0 flex-col"
                    style={option.depth ? { marginInlineStart: `${option.depth}rem` } : undefined}
                  >
                    {option.description ? (
                      <>
                        <span className="truncate">{option.label}</span>
                        <span
                          data-slot="combobox-option-description"
                          className="truncate text-small text-muted-foreground"
                        >
                          {option.description}
                        </span>
                      </>
                    ) : (
                      option.label
                    )}
                  </span>
                  {option.value === value && <CheckIcon className="size-4 shrink-0 text-brand" />}
                </button>
              </div>
            ))}
          </div>
          {footer ? <div className="border-t border-border p-1">{footer}</div> : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

export { Combobox }
